import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MessageSquare, Plus, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AdDisplay from '../components/AdDisplay';
import { AdContext } from '../components/AdContext';
import ToolSelector from '../components/ToolSelector';
import PageTutorial from '../components/PageTutorial';
import PageHero from '../components/PageHero';
import ThemeToggle from '../components/ThemeToggle';
import BrandMark from '../components/BrandMark';

const Forum = () => {
 const { API, user } = useContext(AuthContext);
 const navigate = useNavigate();
 const [categories, setCategories] = useState([]);
 const [threads, setThreads] = useState([]);
 const [selectedCategory, setSelectedCategory] = useState('all');
 const [showDialog, setShowDialog] = useState(false);
 const [newThread, setNewThread] = useState({ category_id: '', title: '' });
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 fetchData();
 }, []);

 useEffect(() => {
 fetchThreads();
 }, [selectedCategory]);

 const fetchData = async () => {
 try {
 const catResponse = await axios.get(`${API}/forum/categories`);
 setCategories(catResponse.data);
 } catch (error) {
 toast.error('Failed to load forum');
 } finally {
 setLoading(false);
 }
 };

 const fetchThreads = async () => {
 try {
 const params = selectedCategory !== 'all' ? { category_id: selectedCategory } : {};
 const response = await axios.get(`${API}/forum/threads`, { params });
 setThreads(response.data);
 } catch (error) {
 toast.error('Failed to load threads');
 }
 };

 const createThread = async () => {
 if (!newThread.category_id || !newThread.title) {
 toast.error('Please fill all fields');
 return;
 }

 try {
 await axios.post(`${API}/forum/threads`, newThread);
 toast.success('Thread created');
 setShowDialog(false);
 setNewThread({ category_id: '', title: '' });
 fetchThreads();
 } catch (error) {
 toast.error('Failed to create thread');
 }
 };

 return (
 <div className="min-h-screen bg-background">
 <div className="bg-background border-b border-border">
 <div className="container mx-auto px-4 py-3 flex items-center justify-between">
 <BrandMark size="md" onClick={() => navigate('/dashboard')} />
 <ThemeToggle />
 </div>
 </div>

 <PageHero
 eyebrow="Community"
 eyebrowIcon={Users}
 title={<>Community <span className="text-amber-brand">forum</span></>}
 description="Talk shop with fellow operators — from cycle troubleshooting to business tactics."
 backTo="/dashboard"
 backLabel="Dashboard"
 testId="forum-hero"
 actions={
 <Dialog open={showDialog} onOpenChange={setShowDialog}>
 <DialogTrigger asChild>
 <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-semibold" data-testid="new-thread-btn">
 <Plus className="w-4 h-4 mr-2" />
 New Thread
 </Button>
 </DialogTrigger>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>Create New Thread</DialogTitle>
 <DialogDescription>Start a new discussion in the community</DialogDescription>
 </DialogHeader>
 <div className="space-y-4 mt-4">
 <div>
 <Label>Category</Label>
 <Select value={newThread.category_id} onValueChange={(v) => setNewThread({...newThread, category_id: v})}>
 <SelectTrigger data-testid="thread-category-select">
 <SelectValue placeholder="Select category" />
 </SelectTrigger>
 <SelectContent>
 {categories.map(cat => (
 <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Thread Title</Label>
 <Input
 value={newThread.title}
 onChange={(e) => setNewThread({...newThread, title: e.target.value})}
 placeholder="What's your question or topic?"
 data-testid="thread-title-input"
 />
 </div>
 <Button onClick={createThread} className="w-full" data-testid="create-thread-btn">
 Create Thread
 </Button>
 </div>
 </DialogContent>
 </Dialog>
 }
 />

 <ToolSelector />

 {/* Forum Header Advertisement */}
 <div className="bg-background border-b border-border">
 <div className="container mx-auto px-4 py-4">
 <AdDisplay location="forum_header" />
 </div>
 </div>

 <div className="container mx-auto px-4 py-8">
 <PageTutorial id="community" />
 {/* Categories */}
 <Card className="mb-6 shadow-lg">
 <CardHeader>
 <CardTitle>Forum Categories</CardTitle>
 <CardDescription>Browse discussions by category</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="flex flex-wrap gap-2">
 <Button
 variant={selectedCategory === 'all' ? 'default' : 'outline'}
 onClick={() => setSelectedCategory('all')}
 data-testid="category-all"
 >
 All Threads
 </Button>
 {categories.map(cat => (
 <Button
 key={cat.id}
 variant={selectedCategory === cat.id ? 'default' : 'outline'}
 onClick={() => setSelectedCategory(cat.id)}
 data-testid={`category-${cat.id}`}
 >
 {cat.name}
 </Button>
 ))}
 </div>
 </CardContent>
 </Card>

 {/* Threads */}
 {loading ? (
 <div className="text-center py-12">
 <p className="text-slate-500">Loading threads...</p>
 </div>
 ) : threads.length === 0 ? (
 <Card className="text-center py-12">
 <CardContent>
 <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
 <p className="text-slate-500 mb-4">No threads yet. Start the conversation!</p>
 <Button onClick={() => setShowDialog(true)} data-testid="create-first-thread-btn">
 Create First Thread
 </Button>
 </CardContent>
 </Card>
 ) : (
 <div className="space-y-4">
 {/* Pinned sponsored thread — pulled from ad_campaigns using the
     `forum_pinned_sponsor` location so admins can rotate multiple
     sponsored posts through the top slot without touching threads. */}
 <SponsoredThreadCard />
 {threads.map(thread => (
 <Card
 key={thread.id}
 className="shadow-md hover:shadow-lg transition-shadow cursor-pointer"
 onClick={() => navigate(`/forum/${thread.id}`)}
 data-testid="thread-card"
 >
 <CardHeader>
 <div className="flex items-start justify-between">
 <div className="flex-1">
 <CardTitle className="text-lg">{thread.title}</CardTitle>
 <CardDescription className="mt-2">
 Started by {thread.user_email} • {new Date(thread.created_at).toLocaleDateString()}
 </CardDescription>
 </div>
 <MessageSquare className="w-5 h-5 text-primary" />
 </div>
 </CardHeader>
 </Card>
 ))}
 </div>
 )}
 </div>
 </div>
 );
};

// ---------------------------------------------------------------------------
// SponsoredThreadCard — pins a rotating ad at the top of the forum feed,
// styled so it reads as a native thread. Admins schedule creatives via
// /admin/ad-slots for the `forum_pinned_sponsor` location. Falls back to
// null (renders nothing) when the pool is empty.
// ---------------------------------------------------------------------------
const SponsoredThreadCard = () => {
 const { API } = React.useContext(AuthContext);
 const { fetchAd } = React.useContext(AdContext);
 const [ad, setAd] = React.useState(null);
 React.useEffect(() => {
   let alive = true;
   fetchAd('forum_pinned_sponsor').then((ads) => {
     if (!alive) return;
     setAd(Array.isArray(ads) && ads.length ? ads[0] : null);
   });
   return () => { alive = false; };
 }, [fetchAd]);
 if (!ad) return null;
 const title = ad.custom_title || ad.name || 'Sponsored';
 const body  = ad.custom_description || '';
 const handleClick = async () => {
   try { await axios.post(`${API}/ads/${ad.campaign_id}/click`); } catch (_) {}
   if (ad.click_url) window.open(ad.click_url, '_blank');
 };
 return (
 <Card
   onClick={handleClick}
   className="shadow-md hover:shadow-lg transition-shadow cursor-pointer border-amber-400/50 bg-amber-50/40 dark:bg-amber-900/10"
   data-testid="forum-sponsored-thread"
 >
 <CardHeader>
 <div className="flex items-start justify-between">
 <div className="flex-1 min-w-0">
   <div className="flex items-center gap-1.5 mb-1">
     <span className="text-[9px] uppercase tracking-widest font-black bg-amber-500 text-navy rounded-full px-2 py-0.5">Sponsored</span>
     {ad.click_url && <span className="text-[10px] text-muted-foreground truncate">{ad.click_url.replace(/^https?:\/\//, '').split('/')[0]}</span>}
   </div>
   <CardTitle className="text-lg">{title}</CardTitle>
   {body && <CardDescription className="mt-2 line-clamp-2">{body}</CardDescription>}
 </div>
 <MessageSquare className="w-5 h-5 text-amber-600 flex-shrink-0" />
 </div>
 </CardHeader>
 </Card>
 );
};

export default Forum;
